import React from 'react'
import { GetStaticProps } from 'next'
import Head from 'next/head'
import Link from 'next/link'
import { builder } from '@builder.io/react'
import * as R from 'ramda'

import { getCars } from '../../asyncLoaders/buttercms'
import { getReviews } from '../../asyncLoaders/nest'
import Layout from '../../components/Layout'
import { HappinessGuaranteeSection } from '../../components/HappinessGuaranteeSection'
import { CarBrandGrid } from '../../components/Builder/components/CarBrandGrid'
import { ReviewCard } from '../../components/ReviewsPage/ReviewsList'
import { FAQ } from '../../components/FAQSection'
import { Button } from '../../components/Button'
import { useQueryParam } from '../../hooks/useQueryParam'
import { Review } from '../../generated/nest-graphql'
import { ServicesSection } from '../../components/ServicesSection'
import { FastAndReliableSection } from '../../components/CityPage/FastAndReliableSection'
import { CitiesCTA } from '../../components/CityPage/CitiesCTA'
import { LogOnMount, Segment } from '../../lib/segment'

export type CityItem = {
  name: string
  path: string
}

export type CityGroup = {
  name: string
  stateAbbreviation: string
  items: CityItem[]
}

builder.init(process.env.BUILDER_API_KEY)

const getCitiesGrouped = async (context: any): Promise<CityGroup[]> => {
  const targeting = { urlPath: '_', device: '_' } as any
  const path = `/cities/${(context.params?.cities as string[])?.join('/') || ''}`
  const defaultPropOptions = {
    userAttributes: {
      ...targeting,
      urlPath: path,
    },
  }
  const builderResults = (await builder.get('cities', defaultPropOptions).promise()) || null
  const builderData = builderResults?.data?.state || {}

  const builderCityData: Record<string, any> = Object.keys(builderData)
    .filter((key) => key.includes('cityData'))
    .reduce((obj, key) => {
      obj[key] = builderData[key]
      return obj
    }, {})

  const citiesSections: CityGroup[] = R.pipe(
    R.toPairs<{
      results: Array<{
        data: {
          cityName: string
          stateAbbreviation: string
          stateName: string
          slug: string
        }
      }>
    }>,
    (pairs) => R.sortBy<any>(R.prop<any>(0), pairs),
    R.map((pair) => {
      const citiesItems: CityItem[] = pair[1]?.results?.map(({ data }) => {
        return {
          name: data.cityName,
          path: `/cities/${data.slug}`,
        }
      })
      const stateName = pair[1]?.results?.[0]?.data?.stateName
      const stateAbbreviation = pair[1]?.results?.[0]?.data?.stateAbbreviation
      return {
        name: stateName,
        stateAbbreviation,
        items: citiesItems,
      }
    })
  )(builderCityData)

  return citiesSections
}

export const getStaticProps: GetStaticProps = async (context) => {
  const citiesGrouped = await getCitiesGrouped(context)

  const cars = (await getCars()) || []

  const reviews = (await getReviews({ limit: 4 }))?.edges || []

  // There are results returned from Builder.io Lets use those
  return {
    props: {
      citiesGrouped,
      carsContent: cars,
      reviews,
    },
    // Revalidate Page Changes after 1 Hour
    revalidate: 3600,
  }
}

interface PageProps {
  reviews: Review[]
  carsContent: any[]
  citiesGrouped: CityGroup[]
}

const CitiesPage = (props: PageProps) => {
  const { carsContent, reviews, citiesGrouped } = props

  console.log("[Builder Props]")
  const query = useQueryParam()
  return (
    <Layout title="Cities We Operate In - NuBrakes Mobile Brake Repair">
      <Head>
        <meta
          name={'description'}
          content={
            'NuBrakes operates in cities all across the United State. Come see if we operate in a city near you.'
          }
        />
      </Head>
      <Segment>
        <LogOnMount eventType="Page Viewed" />
        <div className="flex flex-col items-center p-6 py-10 pb-0">
          <div className="max-w-5xl">
            <div className="flex flex-col text-center justify-center ">
              <h1>BRAKE REPAIR THAT COMES TO YOU</h1>
              <p className="description">
                Discover our extensive network of convenient locations, providing unparalleled mobile brake repair
                services and general car maintenance wherever you need them.
              </p>
              <section className="p-0 pb-4">
                {marketsByState.map(([state, markets]) => (
                  <div key={state} className="flex flex-col">
                    <h2 className="text-center mb-4">{state.toUpperCase()}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mb-4 w-full">
                      {markets.map((marketData) => (
                        <div key={marketData.name} className="flex flex-col items-center md:items-start mb-4">
                          <h3 className="mb-0">NUBRAKES {marketData.name.toUpperCase()}</h3>
                          <a className="text-2xl font-semibold" href={`tel:${marketData.phone}`}>
                            {marketData.phone}
                          </a>
                          <iframe
                            className="border-0 w-full mt-2 mb-4 h-[18rem] lg:h-[22rem]"
                            loading="lazy"
                            allowFullScreen
                            referrerPolicy="no-referrer-when-downgrade"
                            src={`https://www.google.com/maps/embed/v1/place?key=${process.env.NEXT_PUBLIC_MAPS_EMBED_API_KEY}
  &q=${marketData.googleMapsQuery}`}
                          />
                          <div className="description flex flex-col items-center md:items-start pb-0">
                            <p>{marketData.address}</p>
                            <div className="flex flex-row gap-4 flex-wrap justify-center md:justify-start">
                              {marketData.workingHours.map((workingHours) => {
                                return (
                                  <div
                                    key={`${marketData.name}-${workingHours.fromDay}-${workingHours.toDay}`}
                                    className="flex flex-col items-center md:items-start text-center"
                                  >
                                    <p>
                                      {workingHours.fromDay} - {workingHours.toDay}
                                    </p>
                                    <p>
                                      {workingHours.fromHour} - {workingHours.toHour}
                                    </p>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                          <div className="flex w-full">
                            <Link href={{ pathname: `/get-quote/`, query }}>
                              <a className="w-full">
                                <Button className="w-full block py-2 px-6">GET A QUOTE</Button>
                              </a>
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </section>

              <ServicesSection />
            </div>

            <CitiesCTA />

            <CarBrandGrid title="We Offer Auto Repair Services On Nearly Every Model" carContent={carsContent}>
              {<div className="mt-6" />}
            </CarBrandGrid>

            <FastAndReliableSection />

            <section className="pt-0">
              <h2 className="text-center">MOST TRUSTED MOBILE BRAKE REPAIR SERVICE WITH 15,000+ 5 STAR REVIEWS</h2>
              {reviews?.map((review, index) => {
                const isLast = index === reviews.length - 1
                return (
                  <div key={`${review.reviewerName}-${review.comment}`} className={`mb-${isLast ? 0 : 8}`}>
                    <ReviewCard review={review} />
                  </div>
                )
              })}
            </section>

            <section className="pt-6">
              <h2 className="text-center mb-6">Brake repair service FAQ</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FAQ question={`What does your quote include?`}>
                  <p>
                    We pride ourselves on a fully transparent quote and repair process. Each quote includes parts,
                    labor, and taxes. On average, our prices are 15-40% lower than your standard dealership or auto
                    repair shop.
                  </p>
                  <p>
                    After the quote over the phone, our mechanics will then do an official inspection with you in person
                    for recommended repairs.
                  </p>
                </FAQ>
                <FAQ question={`How does the diagnosis work?`}>
                  <p>
                    We will give you a preliminary diagnosis over the phone. Using that diagnosis as a guide, a trained
                    mechanic will then do an inspection with you in person to determine final recommended repairs.
                  </p>
                  <p>
                    If the in-person diagnosis reveals that you do not need any repairs at this time, we will charge a
                    $79 diagnostic fee that can be used as credit for a future repair.
                  </p>
                  <p>
                    If you decline all recommended repairs during the in-person diagnosis, we will charge a $79 service
                    call fee, which cannot be used as credit, to pay for the costs of the in-person visit.
                  </p>
                </FAQ>
                <FAQ question={`Who handles the repairs?`}>
                  <p>
                    All of our technicians have extensive brake repair knowledge with years of experience and are
                    directly employed by NuBrakes, so you know you’re in good hands.
                  </p>
                </FAQ>
                <FAQ question={`What type of parts do you use?`}>
                  <p>
                    We only use premium brake parts that are built to match the OEM (Original Manufacturer Equipment)
                    standards to ensure you’re getting the best braking performance possible.
                  </p>
                </FAQ>
                <FAQ question={`Do you offer a warranty`}>
                  <p>
                    We do! All of our work is backed by our <Link href={'/warranty'}>NuBrakes Guarantee</Link> to ensure
                    your brakes perform as they should each and every day.
                  </p>
                </FAQ>
                <FAQ question={`Do you do any other auto repairs?`}>
                  <p>
                    Our primary focus is brake repairs and brake service. The best part is we can’t upsell you on
                    unnecessary repairs like traditional repair shops because we don’t even offer them!
                  </p>
                </FAQ>
              </div>
            </section>

            <CitiesCTA />

            <section className="pt-6">
              <ul className="list-none p-0 m-0">
                {citiesGrouped.map((state) => {
                  return (
                    <li key={state.name}>
                      <h2>{state.name}</h2>
                      <ul className="list-none p-0 mt-6 mb-8 grid md:grid-cols-2 gap-4">
                        {state.items.map((item, index) => (
                          <li key={item.name} style={{ paddingTop: index === 0 ? 0 : 8 }}>
                            <Link href={item.path} as={{ pathname: item.path, query }}>
                              <a>{`Brake Repair Near Me In ${item.name}, ${state.stateAbbreviation}`}</a>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  )
                })}
              </ul>
            </section>
          </div>
        </div>
        <HappinessGuaranteeSection
          title={'We have a 100% NuBrakes Happiness Guarantee!'}
          description={
            "We want to make the brake repair process simple and frustration-free. If you're not happy with your repair or replacement, we'll do everything we can to fix it. Call or message us any day, any time, and we'll do our best to fix whatever issue you're having."
          }
        />
      </Segment>
    </Layout>
  )
}

export default CitiesPage

enum MarketEnum {
  // Texas (TX)
  Austin = 'Austin',
  Dallas = 'Dallas',
  Houston = 'Houston',
  'San Antonio' = 'San Antonio',

  // Georgia (GA)
  Atlanta = 'Atlanta',

  // Tennessee (TN)
  Nashville = 'Nashville',
  Chattanooga = 'Chattanooga',
  Knoxville = 'Knoxville',

  // Florida (FL)
  Orlando = 'Orlando',
  Miami = 'Miami',
  Tampa = 'Tampa',
  Jacksonville = 'Jacksonville',

  // Illinois (IL)
  Chicago = 'Chicago',
}

type MarketData = {
  market: MarketEnum
  name: string
  phone: string
  address: string
  googleMapsQuery: string
  workingHours: {
    fromDay: string
    toDay: string
    fromHour: string
    toHour: string
  }[]
  stateName: string
  stateAbbreviation: string
}

const marketsData: MarketData[] = [
  // Texas (TX)
  {
    market: MarketEnum.Austin,
    name: 'Austin',
    stateName: 'Texas',
    stateAbbreviation: 'TX',
    phone: '+1 512-640-2560',
    address: '5205 Airport Blvd UNIT 200, Austin, TX 78751, USA',
    googleMapsQuery: 'nubrakes.com+Austin,Austin+TX',
    workingHours: [
      {
        fromDay: 'Monday',
        toDay: 'Friday',
        fromHour: '7:00 AM',
        toHour: '9:00 PM',
      },
      {
        fromDay: 'Saturday',
        toDay: 'Sunday',
        fromHour: '7:00 AM',
        toHour: '9:00 PM',
      },
    ],
  },
  {
    market: MarketEnum.Dallas,
    name: 'Dallas',
    stateName: 'Texas',
    stateAbbreviation: 'TX',
    phone: '+1 469-706-3497',
    address: '902 N Riverfront Blvd Ste B Dallas, TX 75207, USA',
    googleMapsQuery: 'nubrakes.com+Dallas,Dallas+TX',
    workingHours: [
      {
        fromDay: 'Monday',
        toDay: 'Sunday',
        fromHour: '7:00 AM',
        toHour: '9:00 PM',
      },
    ],
  },
  {
    market: MarketEnum.Houston,
    name: 'Houston',
    stateName: 'Texas',
    stateAbbreviation: 'TX',
    phone: '+1 832-706-4115',
    address: '6322 Alder Dr, Houston, Texas 77081, USA', // From Ops
    googleMapsQuery: 'nubrakes.com+Houston,Houston+TX',
    workingHours: [
      {
        fromDay: 'Monday',
        toDay: 'Sunday',
        fromHour: '7:00 AM',
        toHour: '7:00 PM',
      },
    ],
  },
  {
    market: MarketEnum['San Antonio'],
    name: 'San Antonio',
    stateName: 'Texas',
    stateAbbreviation: 'TX',
    phone: '+1 210-794-6444',
    address: '1777 NE Interstate 410 Loop Suite 600, San Antonio, TX 78217, USA',
    googleMapsQuery: 'nubrakes.com+San+Antonio,San+Antonio+TX',
    workingHours: [
      {
        fromDay: 'Monday',
        toDay: 'Sunday',
        fromHour: '7:00 AM',
        toHour: '7:00 PM',
      },
    ],
  },

  // Georgia (GA)
  {
    market: MarketEnum.Atlanta,
    name: 'Atlanta',
    stateName: 'Georgia',
    stateAbbreviation: 'GA',
    phone: '+1 470-467-7736',
    address: '730 Peachtree St NE #570, Atlanta, GA 30308, USA',
    googleMapsQuery: 'nubrakes.com+Atlanta,Atlanta+GA',
    workingHours: [
      {
        fromDay: 'Monday',
        toDay: 'Sunday',
        fromHour: '7:00 AM',
        toHour: '7:00 PM',
      },
    ],
  },

  // Tennessee (TN)
  {
    market: MarketEnum.Nashville,
    name: 'Nashville',
    stateName: 'Tennessee',
    stateAbbreviation: 'TN',
    phone: '+1 629-206-1979',
    address: '40 Burton Hills Blvd #200, Nashville, TN 37215, USA',
    googleMapsQuery: 'nubrakes.com+Nashville,Nashville+GA',
    workingHours: [
      {
        fromDay: 'Monday',
        toDay: 'Sunday',
        fromHour: '7:00 AM',
        toHour: '7:00 PM',
      },
    ],
  },

  // Florida (FL)
  {
    market: MarketEnum.Orlando,
    name: 'Orlando',
    stateName: 'Florida',
    stateAbbreviation: 'FL',
    phone: '+1 689-216-4829',
    address: '618 E South St STE 500, Orlando, FL 32801, USA',
    googleMapsQuery: 'nubrakes.com+Orlando,Orlando+FL',
    workingHours: [
      {
        fromDay: 'Monday',
        toDay: 'Sunday',
        fromHour: '7:30 AM',
        toHour: '7:00 PM',
      },
    ],
  },
  {
    market: MarketEnum.Miami,
    name: 'Miami',
    stateName: 'Florida',
    stateAbbreviation: 'FL',
    phone: '+1 786-780-2495',
    address: '333 SE 2nd Ave Suite 2000, Miami, FL 33131, USA',
    googleMapsQuery: 'nubrakes.com+Miami,Miami+FL',
    workingHours: [
      {
        fromDay: 'Monday',
        toDay: 'Sunday',
        fromHour: '7:00 AM',
        toHour: '7:00 PM',
      },
    ],
  },
  {
    market: MarketEnum.Tampa,
    name: 'Tampa',
    stateName: 'Florida',
    stateAbbreviation: 'FL',
    phone: '+1 813-957-9076',
    address: '100 Ashley Drive South, Tampa, FL 33602, USA',
    googleMapsQuery: 'nubrakes.com+Tampa,Tampa+FL',
    workingHours: [
      {
        fromDay: 'Monday',
        toDay: 'Sunday',
        fromHour: '7:30 AM',
        toHour: '7:00 PM',
      },
    ],
  },

  // TODO-Chicago - Add Chicago when we have it on GMB
]

const marketsByState = R.toPairs(R.groupBy((item) => item.stateName, marketsData))
